import { memo } from 'react';
import { clsx } from 'clsx';
import ProductTile from '../ProductTileView/ProductTile';
import type { CarouselActions } from '../../../core/components/Carousel';
import Carousel from '../../../core/components/Carousel';
import ChevronLeftIcon from '../../../core/components/icons/ChevronLeftIcon';
import ChevronRightIcon from '../../../core/components/icons/ChevronRightIcon';
import Button from '../../../core/components/Button';
import useCarouselActions from '../../../core/hooks/useCarouselActions';
import ErrorBoundary from '../../../core/components/ErrorBoundary';
import type { ProductItem, ProductWithRelatedData } from '../../types';
import SpinnerPage from '../../../core/components/pages/SpinnerPage';
import useProductsWithRelatedData from '../../queries/useProductsWithRelatedData';
import styles from './ProductsCarousel.module.scss';

const ProductsCarousel = ({
    products,
    testClass,
}: {
    products: ProductItem[];
    testClass?: string;
}) => {
    const { nextItem, previousItem, controlsState, handleChangeActions } =
        useCarouselActions({ watchControlState: true });

    const { productsWithRelatedData, loading } =
        useProductsWithRelatedData(products);

    if (!productsWithRelatedData || loading) {
        return <SpinnerPage />;
    }
    return (
        <ProductsCarouselContent
            products={productsWithRelatedData as ProductWithRelatedData[]}
            controlsState={controlsState}
            nextItem={nextItem}
            previousItem={previousItem}
            onChangeActions={handleChangeActions}
            testClass={testClass}
        />
    );
};

export const ProductsCarouselContent = memo(
    ({
        controlsState: { showControls },
        testClass,
        previousItem,
        nextItem,
        products,
        onChangeActions,
    }: {
        products: ProductWithRelatedData[];
        testClass?: string;
        onChangeActions?: (actions: CarouselActions) => void;
        nextItem: () => void;
        previousItem: () => void;
        controlsState: {
            showControls: boolean;
            showLeft: boolean;
            showRight: boolean;
        };
    }) => {
        return (
            <div
                className={styles.attributeCarousel}
                data-test-class={testClass}
            >
                <div
                    className={clsx(styles.previousItemButton, {
                        [styles.hiddenButton]:
                            products.length <= 0 || !showControls,
                    })}
                >
                    <Button iconButton onPress={previousItem}>
                        <ChevronLeftIcon
                            iconLabel="carousel-previous-icon"
                            color="var(--default-icon-color-2)"
                        />
                    </Button>
                </div>
                <Carousel loop onChangeActions={onChangeActions}>
                    {products.map(product => (
                        <div
                            key={product.id}
                            className={styles.variantContainer}
                        >
                            <ErrorBoundary customMessage="Failed to load product">
                                <ProductTile
                                    fullHeight
                                    product={product}
                                    data-test-class="productTile"
                                />
                            </ErrorBoundary>
                        </div>
                    ))}
                </Carousel>
                <div
                    className={clsx(styles.nextItemButton, {
                        [styles.hiddenButton]:
                            products.length <= 0 || !showControls,
                    })}
                >
                    <Button iconButton onPress={nextItem}>
                        <ChevronRightIcon
                            iconLabel="carousel-next-icon"
                            color="var(--default-icon-color-2)"
                        />
                    </Button>
                </div>
            </div>
        );
    }
);

export default memo(ProductsCarousel);
